import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  CUSTOMER_WINDOW_DAYS, PARTNER_WINDOW_DAYS, istTodayIso, addDaysIso,
} from '../utils/bookingWindow';

// Homepage strip: which dates are currently open for online booking, as one
// row of date chips.
//
// Open vs closed comes from the public site's `availability` edge function,
// the same call a customer's browser makes, collapsed to one boolean per
// date: closed when every deck of every cruise reports "closed" (a staff
// closure, or today past the last cut-off), or when no cruise is returned.
// "Closed" is decided server-side before seating is considered, so the party
// size sent here does not affect it; sold-out is deliberately NOT closed.
// Nothing about availability is computed in this app.
//
// Dates past the public window but inside the partner window come from date
// arithmetic alone (no call) and are labelled partner-only.
//
// Read-only. Loads after the rest of Home has painted and never blocks it.

const CACHE_TTL_MS = 60 * 1000;

// Module-level cache keyed by IST date, so a remount within a minute (tab
// switch, navigating back to Home) reuses the last answer.
const cache = new Map();

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const chipLabel = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return `${d} ${MONTHS[m - 1]}, ${WEEKDAYS[dt.getUTCDay()]}`;
};

// One date → 'open' | 'closed'. Throws on a failed call.
const fetchDateState = async (iso) => {
  const { data, error } = await supabase.functions.invoke('availability', {
    body: { date: iso, adults: 1, children: 0 },
  });
  if (error || !data || data.error) {
    throw new Error(data?.error || error?.message || 'availability failed');
  }
  const cruises = data.cruises || [];
  const allClosed = cruises.length === 0 || cruises.every(c => c.status === 'closed');
  return allClosed ? 'closed' : 'open';
};

const buildChips = (today) =>
  Array.from({ length: PARTNER_WINDOW_DAYS + 1 }, (_, offset) => {
    const iso = addDaysIso(today, offset);
    const isPublic = offset <= CUSTOMER_WINDOW_DAYS;
    // state: 'pending' | 'open' | 'closed' | 'partner' | 'error'
    return { iso, state: isPublic ? 'pending' : 'partner' };
  });

export default function AvailabilityStrip() {
  const [chips, setChips] = useState(() => buildChips(istTodayIso()));

  useEffect(() => {
    let cancelled = false;
    const today = istTodayIso();
    const cached = cache.get(today);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      setChips(cached.chips);
      return undefined;
    }

    const fresh = buildChips(today);
    setChips(fresh);

    // Start on the next tick so Home paints first, and so React's dev-only
    // double mount (StrictMode) cancels the first run before it sends anything.
    const timer = setTimeout(() => {
      // All public dates in parallel; each chip settles on its own.
      const working = fresh.map(c => ({ ...c }));
      Promise.all(
        working.map(async (chip, i) => {
          if (chip.state !== 'pending') return;
          try {
            working[i] = { ...chip, state: await fetchDateState(chip.iso) };
          } catch {
            working[i] = { ...chip, state: 'error' };
          }
          if (!cancelled) setChips(working.map(c => ({ ...c })));
        }),
      ).then(() => {
        if (cancelled) return;
        if (working.some(c => c.state === 'open' || c.state === 'closed')) {
          cache.set(today, { chips: working, fetchedAt: Date.now() });
        }
      });
    }, 0);

    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  const publicChips = chips.filter(c => c.state !== 'partner');
  const failed = publicChips.filter(c => c.state === 'error').length;
  const allFailed = failed > 0 && failed === publicChips.length;
  const pending = publicChips.some(c => c.state === 'pending');

  return (
    <section className="obw-card" aria-label="Online booking dates" aria-busy={pending}>
      <div className="obw-head">
        <h2 className="obw-title">Online booking</h2>
        <span className="obw-sub">Dates the booking site is taking right now</span>
      </div>

      {allFailed ? (
        <p className="obw-fail" role="status">Could not load online booking dates. The rest of the page is unaffected.</p>
      ) : (
        <ul className="obw-chips">
          {chips.map(c => (
            <li
              key={c.iso}
              className={`obw-chip obw-chip-${c.state}`}
              title={
                c.state === 'closed' ? 'Closed to online booking'
                : c.state === 'partner' ? 'Open to approved partners only'
                : c.state === 'error' ? 'Could not check this date'
                : undefined
              }
            >
              <span className="obw-chip-date">{chipLabel(c.iso)}</span>
              {c.state === 'partner' && <span className="obw-chip-tag">Partner</span>}
              {c.state === 'error' && <span className="obw-chip-tag">?</span>}
            </li>
          ))}
        </ul>
      )}

      <p className="obw-foot">
        Struck dates are closed online. Dashed dates are inside the partner window only.
        {failed > 0 && !allFailed ? ` Could not check ${failed} ${failed === 1 ? 'date' : 'dates'}.` : ''}
      </p>
    </section>
  );
}
